import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Send, ChevronDown, ChevronUp, Sparkles, RotateCcw } from "lucide-react";
import { ScrollArea } from "./ui/scroll-area";

function TypingIndicator() {
  return (
    <div className="flex items-start gap-3 animate-fade-in">
      <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00d4aa] to-[#6ee7c2] flex items-center justify-center shadow-lg shadow-[#00d4aa]/20">
        <Sparkles className="w-5 h-5 text-[#0a0a0f]" />
      </div>
      <div className="bg-[#16161f] border border-[#00d4aa]/20 rounded-2xl rounded-tl-sm px-5 py-4">
        <div className="typing-indicator">
          <span></span>
          <span></span>
          <span></span>
        </div>
      </div>
    </div>
  );
}

function SourceCard({ source, onClick, isExpanded, onToggle }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 5 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#16161f] border border-[#252530] rounded-xl p-3 hover:border-[#00d4aa]/40 transition-all cursor-pointer hover:shadow-lg hover:shadow-[#00d4aa]/5"
      onClick={onClick}
      data-testid={`source-card-${source.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className="text-xs font-medium text-[#00d4aa] uppercase tracking-wider">
              {source.category}
            </span>
            {source.relevance > 0 && (
              <span className="text-xs text-[#6ee7c2] bg-[#00d4aa]/15 px-2 py-0.5 rounded-full">
                {Math.round(source.relevance * 100)}% match
              </span>
            )}
          </div>
          <h4 className="font-medium text-[#f0f0f5] text-sm truncate">
            {source.title}
          </h4>
        </div>
        <button
          onClick={(e) => { e.stopPropagation(); onToggle(); }}
          className="p-1.5 hover:bg-[#252530] rounded-lg transition-colors"
        >
          {isExpanded ? <ChevronUp className="w-4 h-4 text-[#6ee7c2]" /> : <ChevronDown className="w-4 h-4 text-[#6ee7c2]" />}
        </button>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.p
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="text-sm text-[#a0a0b0] mt-2 leading-relaxed overflow-hidden"
          >
            {source.snippet}
          </motion.p>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function MessageBubble({ message, onSourceClick, isLoading }) {
  const [expandedSources, setExpandedSources] = useState({});
  const isUser = message.role === "user";
  console.log(message, "xxxxxxxxxxxxxxxxxxxxxx");

  // If this message is an assistant placeholder and the system is currently
  // loading, don't render the empty placeholder bubble (we keep the ripple)
  if (!isUser && message.isPlaceholder && isLoading) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className={`flex ${isUser ? "justify-end" : "justify-start"} gap-3`}
      data-testid={`message-${message.id}`}
    >
      {!isUser && (
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00d4aa] to-[#6ee7c2] flex items-center justify-center flex-shrink-0 shadow-lg shadow-[#00d4aa]/20">
          <Sparkles className="w-5 h-5 text-[#0a0a0f]" />
        </div>
      )}

      <div className={`max-w-[80%] ${isUser ? "order-first" : ""}`}>
        <div
          className={`rounded-2xl px-5 py-3 ${
            isUser
              ? "bg-gradient-to-r from-[#00d4aa] to-[#6ee7c2] text-[#0a0a0f] rounded-tr-sm shadow-lg shadow-[#00d4aa]/20"
              : "bg-[#16161f] border border-[#00d4aa]/15 text-[#f0f0f5] rounded-tl-sm"
          }`}
        >
          <p className="text-[15px] leading-relaxed whitespace-pre-wrap">
            {message.content}
          </p>
        </div>
        {/* Per-message confidence (show for assistant messages when present) */}
        {!isUser && message.confidence !== undefined && (
          <div className="mt-2 flex items-center gap-3">
            <span className="text-xs text-[#606070]">Confidence</span>
            <div className="flex-1 h-2 bg-[#16161f] rounded-full overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-[#00d4aa] to-[#6ee7c2] rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${message.confidence}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="text-xs text-[#6ee7c2] font-semibold">
              {message.confidence}%
            </span>
          </div>
        )}

        {!isUser && message.sources && message.sources.length > 0 && (
          <div className="mt-3 space-y-2">
            <p className="text-xs text-[#6ee7c2] uppercase tracking-wider font-medium px-1">
              Sources ({message.sources.length})
            </p>
            {message.sources.map((source) => (
              <SourceCard
                key={source.id}
                source={source}
                onClick={() => onSourceClick(source)}
                isExpanded={expandedSources[source.id]}
                onToggle={() =>
                  setExpandedSources((prev) => ({
                    ...prev,
                    [source.id]: !prev[source.id],
                  }))
                }
              />
            ))}
          </div>
        )}
      </div>

      {isUser && (
        <div className="w-10 h-10 rounded-xl bg-[#252530] flex items-center justify-center text-[#6ee7c2] text-xs font-bold flex-shrink-0 border border-[#00d4aa]/20">
          You
        </div>
      )}
    </motion.div>
  );
}

export default function ChatInterface({ messages, isLoading, onSend, onNewChat, onSourceClick, confidence }) {
  const [input, setInput] = useState("");
  const scrollRef = useRef(null);

  const suggestions = [
    "What's your experience with AI systems?",
    "Tell me about Law Pal",
    "What's your tech stack?",
    "How can I reach you?"
  ];

  useEffect(() => {
    if (scrollRef.current) {
      const scrollContainer = scrollRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  }, [messages, isLoading]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      onSend(input);
      setInput("");
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Suggestion Pills */}
      <div className="px-5 py-4 border-b border-[#00d4aa]/15 bg-[#0c0c14] flex-shrink-0">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs text-[#606070] uppercase tracking-wider font-medium">
            Quick questions
          </p>
          <button
            onClick={onNewChat}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs text-[#6ee7c2] hover:bg-[#00d4aa]/15 rounded-lg transition-colors"
            data-testid="new-chat-button"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            New Chat
          </button>
        </div>
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion, index) => (
            <button
              key={index}
              onClick={() => onSend(suggestion)}
              className="px-4 py-2 text-sm bg-[#16161f] border border-[#252530] text-[#a0a0b0] rounded-full hover:border-[#00d4aa]/50 hover:text-[#6ee7c2] hover:bg-[#00d4aa]/10 transition-all"
              data-testid={`suggestion-${index}`}
            >
              {suggestion}
            </button>
          ))}
        </div>
      </div>

      {/* Messages */}
      <ScrollArea ref={scrollRef} className="flex-1 px-5 py-5 overflow-y-auto">
        <div className="space-y-5">
          <AnimatePresence mode="popLayout">
            {messages.map((message) => (
              <MessageBubble
                key={message.id}
                message={message}
                onSourceClick={onSourceClick}
                isLoading={isLoading}
              />
            ))}
          </AnimatePresence>

          {isLoading && <TypingIndicator />}
        </div>
      </ScrollArea>

      {/* Input Area */}
      <form
        onSubmit={handleSubmit}
        className="px-5 py-5 border-t border-[#00d4aa]/20 bg-gradient-to-r from-[#00d4aa]/5 to-transparent flex-shrink-0"
      >
        <div className="flex items-center gap-3">
          <div className="flex-1 relative">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask about experience, projects, skills..."
              disabled={isLoading}
              className="w-full bg-[#16161f] border-2 border-[#00d4aa]/25 rounded-xl px-5 py-4 text-[#f0f0f5] placeholder:text-[#606070] focus:outline-none focus:border-[#00d4aa] focus:shadow-lg focus:shadow-[#00d4aa]/10 transition-all disabled:opacity-50 text-base"
              data-testid="chat-input"
            />
          </div>
          <button
            type="submit"
            disabled={!input.trim() || isLoading}
            className="p-4 bg-gradient-to-r from-[#00d4aa] to-[#6ee7c2] text-[#0a0a0f] rounded-xl hover:opacity-90 hover:shadow-xl hover:shadow-[#00d4aa]/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="chat-send-button"
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
}
